import React, { useEffect, useCallback, useState } from 'react'

import classNames from 'classnames'
import { isBoolean } from 'lodash'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/Atomic/Molecules/Button'
import { MenuItem, MenuItemProps } from '@/Atomic/Molecules/MenuItem'
import { useIconMenu } from '@/hooks/useIconMenu'
import { useSmartPosition } from '@/hooks/useSmartPosition'

import './DropdownMenu.scss'

export interface DropdownMenuProps {
  menuItems: MenuItemProps[]
  className?: string
  menuClassName?: string
  buttonClassName?: string
  disabled?: boolean
  onOpenChange?: (isOpen: boolean) => void
  trigger?: boolean
  boundarySelector?: string
  /** Render the dropdown panel in a portal at document.body level to escape overflow containers */
  usePortal?: boolean
  /** Icon shown on the trigger button. Defaults to the kebab ("more-horizontal") icon. */
  triggerIcon?: string
  /** Tooltip for the trigger button. Defaults to the generic "more" tooltip. */
  triggerTooltip?: string
  triggerIconColor?: React.ComponentProps<typeof Button>['iconColor']
  /**
   * Called before the menu would open or close. Return `false` to handle the
   * click yourself and keep the menu closed (e.g. to perform a direct action
   * instead of showing the menu); return `true` (or omit the prop) for the
   * normal open/close toggle.
   */
  onBeforeToggle?: () => boolean
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  menuItems,
  className,
  menuClassName,
  buttonClassName,
  disabled,
  onOpenChange,
  trigger,
  boundarySelector = 'main',
  usePortal = false,
  triggerIcon = 'more-horizontal',
  triggerTooltip,
  triggerIconColor,
  onBeforeToggle,
}) => {
  const { t } = useTranslation()
  const { buttonRef, menuIsOpen, toggleMenu, setMenuIsOpen } = useIconMenu()
  const { panelRef, position, hasMeasured } = useSmartPosition({
    isOpen: menuIsOpen,
    triggerRef: buttonRef,
    boundarySelector,
    preferHorizontal: 'left',
    preferVertical: 'bottom',
    listenToScroll: true,
  })

  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    if (menuIsOpen && buttonRef.current) {
      setTriggerRect(buttonRef.current.getBoundingClientRect())
    }
  }, [menuIsOpen, buttonRef])

  useEffect(() => {
    onOpenChange?.(menuIsOpen)
  }, [menuIsOpen, onOpenChange])

  const onTrigger = useCallback(() => {
    if (disabled) {
      return
    }
    if (onBeforeToggle && !onBeforeToggle()) {
      return
    }
    toggleMenu()
  }, [disabled, toggleMenu, onBeforeToggle])

  const onTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onTrigger()
  }

  useEffect(() => {
    if (isBoolean(trigger)) {
      onTrigger()
    }
  }, [trigger, onTrigger])

  return (
    <div
      ref={buttonRef}
      className={classNames('o-dropdown-container', className, {
        open: menuIsOpen,
      })}
    >
      <Button
        icon={triggerIcon}
        iconColor={triggerIconColor}
        variant="icon"
        size="sm"
        tooltip={triggerTooltip ?? t('tooltip.more')}
        onClick={onTriggerClick}
        disabled={disabled}
        buttonClassName={classNames('o-menu-button', buttonClassName)}
      />

      {menuIsOpen &&
        (() => {
          const panelContent = (
            <div
              ref={panelRef}
              className={classNames(
                'o-dropdown-panel',
                `pos-${position}`,
                menuClassName,
                { 'o-dropdown-panel--portal': usePortal },
              )}
              style={
                usePortal && triggerRect
                  ? {
                      visibility: hasMeasured ? 'visible' : 'hidden',
                      position: 'fixed',
                      top: position.startsWith('bottom')
                        ? triggerRect.bottom + 4
                        : undefined,
                      bottom: position.startsWith('top')
                        ? window.innerHeight - triggerRect.top + 4
                        : undefined,
                      left: position.endsWith('right')
                        ? triggerRect.left
                        : undefined,
                      right: position.endsWith('left')
                        ? window.innerWidth - triggerRect.right
                        : undefined,
                    }
                  : { visibility: hasMeasured ? 'visible' : 'hidden' }
              }
            >
              <div className="o-dropdown-list">
                {menuItems.map((item, idx) => (
                  <MenuItem
                    key={`${item.label}-${idx}`}
                    {...item}
                    onClick={(e) => {
                      item.onClick?.(e)
                      setMenuIsOpen(false)
                    }}
                  />
                ))}
              </div>
            </div>
          )

          return usePortal
            ? createPortal(panelContent, document.body)
            : panelContent
        })()}
    </div>
  )
}

export default DropdownMenu
